(() => {
  const supportsFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (!supportsFinePointer.matches) return;
  if (window.Shopify && window.Shopify.designMode) {
    // Avoid fighting the theme editor's own cursor behavior.
    return;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const interactiveSelector = [
    'a[href]',
    'button',
    'summary',
    'label[for]',
    '[role="button"]',
    '[role="link"]',
    '[data-cursor="hover"]',
    'input[type="submit"]',
    'input[type="button"]',
    'input[type="checkbox"]',
    'input[type="radio"]',
    'select',
    '.shopify-payment-button__button',
  ].join(',');

  const textSelector = [
    'input[type="text"]',
    'input[type="search"]',
    'input[type="email"]',
    'input[type="tel"]',
    'input[type="url"]',
    'input[type="password"]',
    'input[type="number"]',
    'textarea',
    '[contenteditable="true"]',
  ].join(',');

  const root = document.createElement('div');
  root.className = 'custom-cursor';
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = '<div class="custom-cursor__ring"></div><div class="custom-cursor__dot"></div>';

  const ring = root.querySelector('.custom-cursor__ring');
  const dot = root.querySelector('.custom-cursor__dot');

  /* Use Popover API so the cursor lives in the top layer and is never
     clipped by dialogs (e.g. the cart drawer). Falls back to body append. */
  let inTopLayer = false;

  const promoteToTopLayer = () => {
    if (typeof root.showPopover !== 'function') return false;
    try {
      root.hidePopover();
      root.showPopover();
      return true;
    } catch (_) {
      return false;
    }
  };

  const mount = () => {
    if (typeof root.showPopover === 'function') {
      root.setAttribute('popover', 'manual');
      document.body.appendChild(root);
      try {
        root.showPopover();
        inTopLayer = true;
      } catch (_) {
        /* popover failed — cursor stays in normal flow */
      }
    } else {
      document.body.appendChild(root);
    }
    document.documentElement.classList.add('custom-cursor-enabled');
    if (inTopLayer) {
      document.documentElement.classList.add('custom-cursor-in-toplayer');
    }
  };

  /* Safety: if the custom cursor element is not visible after mount,
     remove the cursor-hiding class so users get the default cursor back. */
  const mountWithFallback = () => {
    try {
      mount();
      if (!root.isConnected) {
        throw new Error('cursor element not connected');
      }
    } catch (_) {
      document.documentElement.classList.remove('custom-cursor-enabled');
      document.documentElement.classList.remove('custom-cursor-in-toplayer');
    }
  };

  if (document.body) {
    mountWithFallback();
  } else {
    document.addEventListener('DOMContentLoaded', mountWithFallback, { once: true });
  }

  /* Re-promote cursor to top of the top layer whenever a dialog opens.
     dialog.showModal() places the dialog above the cursor popover, making
     the cursor invisible. By re-showing the popover we move it back on top. */
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.attributeName === 'open' && m.target.tagName === 'DIALOG' && m.target.hasAttribute('open')) {
        if (inTopLayer) {
          // Small delay to ensure the dialog is fully in the top layer first
          requestAnimationFrame(() => promoteToTopLayer());
        }
        break;
      }
    }
  });
  observer.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });

  const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  const ringPos = { x: target.x, y: target.y };
  const dotPos = { x: target.x, y: target.y };
  const ringEase = reduceMotion.matches ? 1 : 0.18;
  const dotEase = reduceMotion.matches ? 1 : 0.55;

  let rafId = null;
  let ready = false;

  const render = () => {
    ringPos.x += (target.x - ringPos.x) * ringEase;
    ringPos.y += (target.y - ringPos.y) * ringEase;
    dotPos.x += (target.x - dotPos.x) * dotEase;
    dotPos.y += (target.y - dotPos.y) * dotEase;

    ring.style.transform = `translate(${ringPos.x}px, ${ringPos.y}px) translate(-50%, -50%)`;
    dot.style.transform = `translate(${dotPos.x}px, ${dotPos.y}px) translate(-50%, -50%)`;

    rafId = requestAnimationFrame(render);
  };

  const start = () => {
    if (rafId != null) return;
    rafId = requestAnimationFrame(render);
  };

  const stop = () => {
    if (rafId == null) return;
    cancelAnimationFrame(rafId);
    rafId = null;
  };

  const setState = (state) => {
    if (state) {
      root.dataset.state = state;
    } else {
      delete root.dataset.state;
    }
  };

  const updateStateFromElement = (el) => {
    if (!el) return setState(null);
    if (el.closest && el.closest(textSelector)) return setState('text');
    if (el.closest && el.closest(interactiveSelector)) return setState('hover');
    setState(null);
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      target.x = event.clientX;
      target.y = event.clientY;

      if (!ready) {
        ringPos.x = dotPos.x = target.x;
        ringPos.y = dotPos.y = target.y;
        ready = true;
        root.dataset.ready = 'true';
      }
      updateStateFromElement(event.target);
      start();
    },
    { passive: true }
  );

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    setState('down');
  });

  document.addEventListener('pointerup', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    updateStateFromElement(event.target);
  });

  document.addEventListener('mouseleave', () => {
    root.dataset.hidden = 'true';
  });

  document.addEventListener('mouseenter', () => {
    delete root.dataset.hidden;
  });

  window.addEventListener('blur', () => {
    root.dataset.hidden = 'true';
    stop();
  });

  window.addEventListener('focus', () => {
    delete root.dataset.hidden;
    start();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stop();
    } else {
      start();
    }
  });

})();
